import { useState } from "react";
import { endOfMonth, format, startOfMonth, subDays } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CashFlowFilters } from "@/Api/financialCashFlow";
import { CashFlowSituation } from "@/types/cashFlow";
import { FINANCIAL_TRANSACTION_CONST } from "@/constants/FinancialTransactionTypeConst";

type FiltersProps = {
  onSearch: (filters: CashFlowFilters) => void;
};

const PERIODS = [
  { value: "today", label: "Hoje" },
  { value: "7", label: "Últimos 7 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "month", label: "Este mês" },
  { value: "custom", label: "Personalizado" },
];

const PER_PAGE_OPTIONS = [10, 25, 50, 100, 200];
export const DEFAULT_PER_PAGE = 50;

const toInputDate = (date: Date) => format(date, "yyyy-MM-dd");

const getPeriodRange = (period: string): [string, string] => {
  const today = new Date();
  switch (period) {
    case "today":
      return [toInputDate(today), toInputDate(today)];
    case "7":
      return [toInputDate(subDays(today, 7)), toInputDate(today)];
    case "30":
      return [toInputDate(subDays(today, 30)), toInputDate(today)];
    default:
      return [toInputDate(startOfMonth(today)), toInputDate(endOfMonth(today))];
  }
};

export const getDefaultCashFlowFilters = (): CashFlowFilters => {
  const [start, end] = getPeriodRange("month");
  return {
    date_start_payment: start,
    date_end_payment: end,
    per_page: DEFAULT_PER_PAGE,
    situation: "reconciled",
  };
};

export const Filters = ({ onSearch }: FiltersProps) => {
  const [period, setPeriod] = useState("month");
  const [[dateStart, dateEnd], setDates] = useState(getPeriodRange("month"));
  const [typeId, setTypeId] = useState("all");
  const [situation, setSituation] = useState<CashFlowSituation>("reconciled");
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE.toString());

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    if (value !== "custom") setDates(getPeriodRange(value));
  };

  const handleSearch = () => {
    onSearch({
      date_start_payment: dateStart,
      date_end_payment: dateEnd,
      transaction_type_id: typeId === "all" ? undefined : typeId,
      per_page: Number(perPage),
      situation,
    });
  };

  return (
    <div className="flex flex-wrap items-end gap-3 mb-6 bg-white dark:bg-gray-800 rounded-lg border p-4">
      <div className="w-full sm:w-44">
        <label className="text-sm font-medium mb-1 block">Período</label>
        <Select value={period} onValueChange={handlePeriodChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {period === "custom" && (
        <>
          <div className="w-full sm:w-40">
            <label className="text-sm font-medium mb-1 block">De</label>
            <Input
              type="date"
              value={dateStart}
              onChange={(e) => setDates([e.target.value, dateEnd])}
            />
          </div>
          <div className="w-full sm:w-40">
            <label className="text-sm font-medium mb-1 block">Até</label>
            <Input
              type="date"
              value={dateEnd}
              onChange={(e) => setDates([dateStart, e.target.value])}
            />
          </div>
        </>
      )}

      <div className="w-full sm:w-44">
        <label className="text-sm font-medium mb-1 block">Tipo</label>
        <Select value={typeId} onValueChange={setTypeId}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem
              value={FINANCIAL_TRANSACTION_CONST.ACCOUNT_RECEIVABLE.toString()}
            >
              Receber
            </SelectItem>
            <SelectItem
              value={FINANCIAL_TRANSACTION_CONST.ACCOUNT_PAYABLE.toString()}
            >
              Pagar
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-full sm:w-44">
        <label className="text-sm font-medium mb-1 block">Situação</label>
        <Select
          value={situation}
          onValueChange={(value) => setSituation(value as CashFlowSituation)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="reconciled">Conciliados</SelectItem>
            <SelectItem value="pending">Não conciliados</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-full sm:w-36">
        <label className="text-sm font-medium mb-1 block">Quantidade</label>
        <Select value={perPage} onValueChange={setPerPage}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PER_PAGE_OPTIONS.map((option) => (
              <SelectItem key={option} value={option.toString()}>
                {option} por página
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button onClick={handleSearch}>Filtrar</Button>
    </div>
  );
};
